import ImageContextBase from "./imageContextBase";
import { ANNOTATION_MODES, ANNOTATION_COLORS, GRAYSCALE_MODES, addMode, editMode ,PRELOAD_OPTIONS } from "../config/config";
import { defaultColorSettings, generateFlatImage } from "../canvas/canvasUtils";
import store from "../store";
import { defaultTransform } from "../canvas/geometry";
import { sortImagesByTimespan } from "../helpers/utils";
import Magnifier from "../canvas/annotations/magnifier";

export default class ImageContext extends ImageContextBase {
  constructor(image, images, exam) {
    super(exam, "imageBrowserBody", "Image Browser");

    this.isAnnotating = true;
    this.annotationMode = ANNOTATION_MODES.None;
    this.annotationColor = ANNOTATION_COLORS.Black;
    this.annotationLineWidth = 5.5;
    this.annotationTextSize = (store.getters.axis5DefaultFontSize) ? Math.max(Math.min(40 + (store.getters.axis5DefaultFontSize * 10), 100), 10) : 40;
    this.annotations = [];
    this.activeAnnotation = null;
    this.mouseWheelZoom = false;

    this.image = image;
    this.images = sortImagesByTimespan(images);

    this.imageMask = null;
    this.deepDeeHighlight = false;
    this.deepDeeAnnotation = null;
    this.showDeepDeeAnnotation = false;
    this.diagnosisText = "";
    this.diagnosisColor = "";

    this.isFlipped = false;
    this.isFlipped90 = false;
    this.isDragLock = false;

    this.transform = defaultTransform();

    this.rgbSettings = null;
    this.webglCanvas = this.hasWebgl ? document.createElement("canvas") : null;
    this.saveCanvas = document.createElement("canvas");
    this.flattenedImageReady = false;
    this.imageColorSettings = defaultColorSettings(null, exam.isOptos);

    this.device = store.getters.devices.find(d => d.devSrNo === exam.devSrNo);
    this.deviceConfigs = this.device && this.device.configs || [];
    this.deviceCalibration = null;
    this.defaultZoom = this.device && this.device.defaultZoomFactor || null;

    this.showMeasurementSection = true;
    this.showPlaybackToolbar = true;
    this.showImageDrawer = true;

    this.magnifier = new Magnifier({});
    this.isMagnifying = false;
    this.preLoadOption = store.getters.preLoadOption;

    this.shapeFillColor = ANNOTATION_COLORS.Black;
  }

  startupTask() {
    super.startupTask();
    if (this.exam.isOptos) this.toggleRGB();
    if (this.device.defaultZoomFactor) this.setDefaultZoom();
    return this.refreshImages();
  }

  refreshImages() {
    if(this.preLoadOption == PRELOAD_OPTIONS.preLoad2Steps){
      let mainImages = [];
      mainImages.push(this.image);
      let images = this.images;
      return store.dispatch("preLoad_2Steps", { images, mainImages});       
    }
    else if(this.preLoadOption == PRELOAD_OPTIONS.preLoadAll){
      return store.dispatch("preload_all", this.images);   
    }
    else{ // PreLoadNone
      return store.dispatch("loadImages_full_thumb", this.images);   
    }    
  }
  
  selectCalibration(name) {
    if (!name) return;
    let matchingConfig = this.deviceConfigs.find(v => v.desc === name);
    if (!matchingConfig) return;
    this.deviceCalibration = matchingConfig;
  }

  selectImage(image) {
    this.image = image;
    this.clearAnnotations();
    this.resetAnnotationSettings();
    this.isFlipped = false;
    this.isFlipped90 = false;
    this.resetColorSettings();
    this.setFlattenedImageReady(false);
    if (this.exam.isOptos) this.toggleRGB();
  }

  setDiagnosisText(text) {
    this.diagnosisText = text;
  }
  setDiagnosisColor(color) {
    this.diagnosisColor = color;
  }

  setImageMask(mask) {
    this.imageMask = mask;
  }
  toggleDeepDeeHighlight() {
    this.deepDeeHighlight = !this.deepDeeHighlight;
  }

  setDeepDeeAnnotation(annotation) {
    this.deepDeeAnnotation = annotation;
  }
  toggleDeepDeeAnnotation() {
    this.showDeepDeeAnnotation = !this.showDeepDeeAnnotation;
  }

  // #region annotations
  toggleIsAnnotating() {
    this.isAnnotating = !this.isAnnotating;
  }
  resetAnnotationSettings() {
    this.annotationMode = ANNOTATION_MODES.None;
    this.annotationColor = ANNOTATION_COLORS.Black;
  }
  clearAnnotationMode() {
    this.annotationMode = ANNOTATION_MODES.None;
  }
  updateAnnotationColor(color) {
    this.annotationColor = color;
  }
  updateAnnotationTextSize(size) {
    this.annotationTextSize = size || 40;
    if (this.activeAnnotation)
      this.activeAnnotation.updateTextSize(size);
  }
  toggleIsDragLock()   {
    this.isDragLock = !this.isDragLock;
  }
  toggleAnnotationMode(mode) {
    if (!this.deepDeeHighlight) {
      this.clearActiveAnnotation();
      if ((this.isFlipped || this.isFlipped90) && (addMode(mode) || editMode(mode))) return;
      if (this.annotationMode == mode) this.clearAnnotationMode();
      else this.annotationMode = mode;
    }
  }
  setAnnotationMode(mode) {
    this.annotationMode = mode;
  }
  clearAnnotations() {
    this.annotations = [];
    this.clearActiveAnnotation();
  }
  clearActiveAnnotation() {
    if (this.activeAnnotation) this.activeAnnotation.deactivate();
    this.activeAnnotation = null;
  }
  setActiveAnnotation(annotation) {
    this.activeAnnotation = annotation;
  }
  addAnnotation(annotation) {
    this.annotations.push(annotation);
  }
  deleteAnnotation(index) {
    this.annotations.splice(index, 1);
  }
  updateLineWidth(width) {
    this.annotationLineWidth = width;
    if (this.activeAnnotation)
      this.activeAnnotation.setLineWidth(width);
  }
  setIsMagnifying(val) {
    this.isMagnifying = val;
  }
  updateMagnifier({offsetX, offsetY, x, y}) {
    this.magnifier.updatePosition(
      offsetX,
      offsetY,
      x,
      y
    );
  }
  mouseupMagnifier() {
    this.magnifier.mouseup();
  }
  mousedownMagnifier() {
    this.magnifier.mousedown();
    if (!this.flattenedImageReady) {
      generateFlatImage(this);
      this.magnifier.saveCanvas = this.saveCanvas;
    }
  }

  updateShapeFillColor(color) {
    this.shapeFillColor = color;
  }
  // #endregion

  // #region transformations
  toggleFlipped() {
    if (!this.deepDeeHighlight)
      this.isFlipped = !this.isFlipped;
  }
  toggleFlipped90() {
    if (!this.deepDeeHighlight)
      this.isFlipped90 = !this.isFlipped90;
  }
  setInitialAnnotationSizes() {
    // Only set initial size if axisSetting for size is not present.
    if (!store.getters.axis5DefaultFontSize) {
      let zoom = this.transform && this.transform.zoom || 1;
      let readableTextSize = Math.min(Math.ceil((20 / zoom) / 10) * 10, 100);
      this.annotationTextSize = Math.max(this.annotationTextSize, readableTextSize);
    }
  }
  setTransform(transform) {
    this.transform = transform;
  }
  
  toggleMouseWheelZoom() {
    this.mouseWheelZoom = !this.mouseWheelZoom;
  }

  setDefaultZoom() {
    this.defaultZoom =  this.device.defaultZoomFactor;
  }

  // #endregion

  // #region colorSettings
  resetColorSettings() {
    this.imageColorSettings = defaultColorSettings(null, this.exam.isOptos)
    this.rgbSettings = null;
  }
  setFlattenedImageReady(ready) {
    this.flattenedImageReady = ready;
  }
  toggleGrayscaleMode(mode) {
    if (this.imageColorSettings.grayscaleMode === mode) this.clearGrayscaleMode();
    else this.imageColorSettings.grayscaleMode = mode;
  }
  clearGrayscaleMode() {
    this.imageColorSettings.grayscaleMode = GRAYSCALE_MODES.None;
  }
  updateBrightness(brightness) {
    this.imageColorSettings.brightness = brightness;
  }
  updateContrast(contrast) {
    this.imageColorSettings.contrast = contrast;
  }
  updateGamma(gamma) {
    this.imageColorSettings.gamma = gamma;
  }
  updateGreenBrightness(brightness) {
    this.imageColorSettings.greenBrightness = brightness;
  }
  updateGreenContrast(contrast) {
    this.imageColorSettings.greenContrast = contrast;
  }
  // #endregion

  // #region rgb
  startRGB() {
    if (this.grayscaleMode === GRAYSCALE_MODES.REC709) this.clearGrayscaleMode();
    let view = this.getCurrentRGBIndex();
    let isFlipped = this.isFlipped;
    let isOptos = this.exam.isOptos;
    this.rgbSettings = {
      expanded: true,
      sync: true,
      greenSync: true,
      view: view,
      views: [
        {
          ...defaultColorSettings(GRAYSCALE_MODES.None, isOptos),
          isFlipped,
          index: 0,
          alignTop: false,
          borderColor: "white",
          title: "RGB",
          icon: "rgb-icon.png"
        },
        {
          ...defaultColorSettings(GRAYSCALE_MODES.Red, true, isOptos),
          isFlipped,
          index: 1,
          alignTop: false,
          borderColor: "red",
          title: "Red Channel",
          icon: "red-icon.png"
        },
        {
          ...defaultColorSettings(GRAYSCALE_MODES.Green, true, isOptos),
          isFlipped,
          index: 2,
          alignTop: !isOptos,
          borderColor: "green",
          title: "Green Channel",
          icon: "green-icon.png"
        }
      ]
    };
    if (!isOptos) {
      this.rgbSettings.views.push({
        ...defaultColorSettings(GRAYSCALE_MODES.Blue, true, isOptos),
        isFlipped,
        index: 3,
        alignTop: true,
        borderColor: "blue",
        title: "Blue Channel",
        icon: "blue-icon.png"
      });
    }
  }
  getRGBIndex(mode) {
    switch (mode) {
      case GRAYSCALE_MODES.Red: return 1;
      case GRAYSCALE_MODES.Green: return 2;
      case GRAYSCALE_MODES.Blue: return 3;
      default: return 0;
    }
  }
  getCurrentRGBIndex() {
    return this.getRGBIndex(this.grayscaleMode);
  }
  clearRGB() {
    this.rgbSettings = null;
  }
  toggleRGB() {
    if (this.rgbSettings) this.clearRGB();
    else this.startRGB();
  }
  toggleRGBSync() {
    if (!this.rgbSettings) return;
    this.rgbSettings.sync = !this.rgbSettings.sync;
  }
  toggleGreenSync() {
    if (!this.rgbSettings) return;
    this.rgbSettings.greenSync = !this.rgbSettings.greenSync;
  }
  setRGBExpanded(expanded) {
    if (!this.rgbSettings) return;
    this.rgbSettings.expanded = expanded;
  }
  selectRGBView(index) {
    if (!this.rgbSettings) return;
    this.rgbSettings.view = index;
  }
  replicateCurrentRGBToMain() {
    if (!this.rgbSettings) return;
    let currentRGB = this.rgbSettings.views[this.rgbSettings.view];
    this.isFlipped = currentRGB.isFlipped;
    this.imageColorSettings.grayscaleMode = currentRGB.grayscaleMode;
    this.imageColorSettings.brightness = currentRGB.brightness;
    this.imageColorSettings.contrast = currentRGB.contrast;
    this.imageColorSettings.gamma = currentRGB.gamma;
    this.imageColorSettings.greenBrightness = currentRGB.greenBrightness;
    this.imageColorSettings.greenContrast = currentRGB.greenContrast;
  }
  replicateMainToCurrentRGB() {
    if (!this.rgbSettings) return;
    let currentRGB = this.rgbSettings.views[this.rgbSettings.view];
    currentRGB.isFlipped = this.isFlipped;
    currentRGB.brightness = this.imageColorSettings.brightness;
    currentRGB.contrast = this.imageColorSettings.contrast;
    currentRGB.gamma = this.imageColorSettings.gamma;
    currentRGB.greenBrightness = this.imageColorSettings.greenBrightness;
    currentRGB.greenContrast = this.imageColorSettings.greenContrast;
  }
  replicateMainToAllRGB() {
    if (!this.rgbSettings) return;
    this.rgbSettings.views.forEach(view => {
      view.isFlipped = this.isFlipped;
      view.brightness = this.imageColorSettings.brightness;
      view.contrast = this.imageColorSettings.contrast;
      view.gamma = this.imageColorSettings.gamma;
    });
  }
  replicateGreenMainToAllRGB() {
    if (!this.rgbSettings) return;
    this.rgbSettings.views.forEach(view => {
      view.greenBrightness = this.imageColorSettings.greenBrightness;
      view.greenContrast = this.imageColorSettings.greenContrast;
    });
  }
  // #endregion

}